/**
 * COREX brand marks: the "X" mark (two crossing strokes, vector) and the COREX wordmark (the official artwork in
 * /brand). Brand colour is violet #7C3AED on navy #1E1B4B; every mark also takes `currentColor` (tone="current") so it
 * can sit white on the brand panel or grey where the UI asks for it.
 */
import { useId, type CSSProperties } from 'react'

export const BRAND_RED = '#7C3AED'
export const BRAND_NAVY = '#1E1B4B'
export const WORDMARK_URL = '/brand/corex-wordmark.png'

type BrandTone = 'brand' | 'white' | 'current'
const fillOf = (tone: BrandTone) => (tone === 'brand' ? BRAND_RED : tone === 'white' ? '#ffffff' : 'currentColor')

/** The mark's outline in the viewBox "40 110 430 290" (print pages draw it without React): the X's two strokes. */
export const ALIENS_MARK_PATH = 'M100 120h90l220 270h-90Z M320 120h90L190 390h-90Z'
const STROKE_A = 'M100 120h90l220 270h-90Z'
const STROKE_B = 'M320 120h90L190 390h-90Z'

/** The X on its own. */
export function AliensMark({ className, tone = 'brand', title = 'COREX' }: { className?: string; tone?: BrandTone; title?: string }) {
  return (
    <svg viewBox="40 110 430 290" className={`keep-color ${className ?? ''}`} role="img" aria-label={title}>
      <path fill={tone === 'brand' ? BRAND_NAVY : fillOf(tone)} d={STROKE_A} />
      <path fill={fillOf(tone)} fillOpacity={tone === 'brand' ? 1 : 0.78} d={STROKE_B} />
    </svg>
  )
}

/** The COREX wordmark: the artwork itself in brand tone, its shape filled with one colour otherwise. */
export function AliensWordmark({ className, tone = 'brand', style }: { className?: string; tone?: BrandTone; style?: CSSProperties }) {
  const id = useId().replace(/:/g, '')
  return (
    <svg viewBox="0 0 900 152" className={`keep-color ${className ?? ''}`} style={style} role="img" aria-label="COREX" preserveAspectRatio="xMidYMid meet">
      {tone === 'brand' ? <image href={WORDMARK_URL} width="900" height="152" /> : (
        <>
          <mask id={`wm${id}`} style={{ maskType: 'alpha' }}><image href={WORDMARK_URL} width="900" height="152" /></mask>
          <rect width="900" height="152" fill={fillOf(tone)} mask={`url(#wm${id})`} />
        </>
      )}
    </svg>
  )
}

/** Wordmark + tagline, the tagline under the wordmark's end like the artwork. Size it with the width. */
export function AliensLockup({ className, tone = 'brand', tagline = true }: { className?: string; tone?: BrandTone; tagline?: boolean }) {
  return (
    <div className={`flex flex-col items-end ${className ?? ''}`} dir="ltr">
      <AliensWordmark tone={tone} className="block w-full" />
      {tagline && (
        <span className="mt-[4%] block w-[54%] text-end font-sans font-medium tracking-[0.2em]" style={{ color: tone === 'brand' ? BRAND_NAVY : fillOf(tone), fontSize: 'clamp(9px, 1.3vw, 18px)' }}>
          Shipping Management System
        </span>
      )}
    </div>
  )
}
